import { useState, useEffect } from "react";
import {
  getAuth,
  onAuthStateChanged,
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import {
  doc,
  addDoc,
  serverTimestamp,
  collection,
  getDocs,
  updateDoc,
  arrayUnion,
  setDoc,
  getDoc,
} from "firebase/firestore";
import { db } from "../firebase/firebase";
import { logToSystem } from "../utils/systemLogging";


// Session expiry time: 2 days
const SESSION_EXPIRY = 2 * 24 * 60 * 60 * 1000;

export const useAuth = () => {
  const [isSignedIn, setIsSignedIn] = useState<boolean>(() => {
    return localStorage.getItem("isSignedIn") === "true";
  });

  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const auth = getAuth();
  const [orgId, setOrgId] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true); // Start loading immediately

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        console.log("Auth state updated:", currentUser.email);
        setUser(currentUser);
        setIsSignedIn(true);

        // Fetch orgId for persisted session
        try {
          const userRef = doc(db, "users", currentUser.uid);
          const userDoc = await getDoc(userRef);

          if (userDoc.exists()) {
            const userData = userDoc.data();
            let validOrgId = userData.organizationId;

            if (!validOrgId && userData.adminId) {
              const adminRef = doc(db, "admins", userData.adminId);
              const adminDoc = await getDoc(adminRef);
              if (adminDoc.exists()) {
                validOrgId = adminDoc.data().organizationId;
              }
            }
            if (validOrgId) {
              setOrgId(validOrgId);
            }
          }
        } catch (error) {
          console.error("Error fetching orgId in useAuth:", error);
        }

        const newExpiry = Date.now() + SESSION_EXPIRY;
        localStorage.setItem("sessionExpiry", newExpiry.toString());
        localStorage.setItem("isSignedIn", "true");
      } else {
        setUser(null);
        setOrgId(null);
        setIsSignedIn(false);
        localStorage.removeItem("sessionExpiry");
        localStorage.removeItem("isSignedIn");
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [auth]);  // Depend on `auth`



  const signUp = async (email: string, password: string, userData: { firstName: string; lastName: string; phone: string }) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);

      // Save user data temporarily
      localStorage.setItem("pendingUser", JSON.stringify({ uid: result.user.uid, ...userData, email }));

      return result; // Ensure this function returns the result
    } catch (error: any) {
      console.error("Sign Up Error:", error.message);
      throw error;
    }
  };
  const signIn = async (email: string, password: string, navigate: (path: string) => void) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      const user = result.user;

      // Fetch user data from `users` collection
      const userRef = doc(db, "users", user.uid);
      const userDoc = await getDoc(userRef);

      if (!userDoc.exists()) {
        throw new Error("User not found. Please contact support.");
      }

      const userData = userDoc.data();

      // Validate organizationId
      let validOrganizationId = userData.organizationId || null;

      // If the user has an adminId, validate the `admins` collection
      if (userData.adminId) {
        const adminRef = doc(db, "admins", userData.adminId);
        const adminDoc = await getDoc(adminRef);

        if (adminDoc.exists()) {
          const adminData = adminDoc.data();

          // Use admin's organizationId if user doesn't have one
          if (!validOrganizationId && adminData.organizationId) {
            validOrganizationId = adminData.organizationId;
          }
        }
      }

      // If no valid organizationId is found, throw an error
      if (!validOrganizationId) {
        throw new Error("No valid organization found. Please contact support.");
      }
      console.log("Before setting orgId:", orgId);

      // Update state FIRST
      setUser(user);
      setOrgId(validOrganizationId);
      setIsSignedIn(true);

      // Set session expiry and localStorage
      const expiryTime = Date.now() + SESSION_EXPIRY;
      localStorage.setItem("sessionExpiry", expiryTime.toString());
      localStorage.setItem("isSignedIn", "true");

      // Navigate LAST - after all state is set
      navigate(`/organization/${validOrganizationId}`);

      console.log("User signed in:", user.email);
    } catch (error: any) {
      console.error("Sign In Error:", error.message);
      throw error;
    }
  };



  const createOrganization = async (orgName: string, navigate: (path: string) => void) => {
    try {
      console.log("Starting organization creation...");

      const storedUser = localStorage.getItem("pendingUser");
      if (!storedUser) {
        throw new Error("User data not found. Please register again.");
      }

      const adminData = JSON.parse(storedUser);

      const validatedAdminData = {
        uid: adminData.uid || "",
        firstName: adminData.firstName || "",
        lastName: adminData.lastName || "",
        email: adminData.email || "",
        phone: adminData.phone || "",
        role: "admin",
        dept: adminData.dept || "Management",
      };

      // Create the organization document
      const orgRef = await addDoc(collection(db, "organization"), {
        name: orgName,
        createdAt: serverTimestamp(),
        admin: validatedAdminData.uid,
        members: [
          {
            id: validatedAdminData.uid,
            email: validatedAdminData.email,
            phone: validatedAdminData.phone,
            role: validatedAdminData.role,
            dept: validatedAdminData.dept,
            firstName: validatedAdminData.firstName,
            lastName: validatedAdminData.lastName,
            // Without this the founding admin has no joining date, which
            // leave accrual and attendance reporting both rely on.
            joiningDate: new Date().toISOString().split("T")[0],
          },
        ],
        modules: [
          "projects",
          "timesheet",
          "attendance",
          "leaveManagement",
          "performanceReviews",
          "payroll",
        ],
      });

      console.log("Organization created with ID:", orgRef.id);

      // Create subscription document with initial status as "Inactive"
      const subscriptionRef = await addDoc(collection(db, "subscriptions"), {
        orgId: orgRef.id,
        status: "Inactive",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });


      // Add organization ID to the admin's document in the `admin` collection
      const adminRef = doc(db, "admins", validatedAdminData.uid);
      await setDoc(
        adminRef,
        {
          id: validatedAdminData.uid,
          firstName: validatedAdminData.firstName,
          lastName: validatedAdminData.lastName,
          email: validatedAdminData.email,
          phone: validatedAdminData.phone,
          role: validatedAdminData.role,
          dept: validatedAdminData.dept,
          organizationId: orgRef.id,
        },
        { merge: true }
      );

      // Save the admin's data in a separate document in the `users` collection
      const userRef = doc(db, "users", validatedAdminData.uid);
      await setDoc(
        userRef,
        {
          id: validatedAdminData.uid,
          firstName: validatedAdminData.firstName,
          lastName: validatedAdminData.lastName,
          email: validatedAdminData.email,
          phone: validatedAdminData.phone,
          role: validatedAdminData.role,
          dept: validatedAdminData.dept,
          organizationId: orgRef.id, // Reference to the organization
          adminId: validatedAdminData.uid, // Store admin ID here
          createdAt: serverTimestamp(),
        },
        { merge: true }
      );


      // Remove pending user data from localStorage
      localStorage.removeItem("pendingUser");

      // Navigate to the new organization's dashboard
      navigate(`/organization/${orgRef.id}`);

    } catch (error: any) {
      console.error("Error creating organization:", error.message);
      throw new Error("Failed to create the organization. Please try again.");
    }
  };



  const logout = async () => {
    try {
      const currentUser = auth.currentUser;
      if (currentUser && orgId) {
          // Log Logout Event
          try {
            let userName = currentUser.displayName || "User";

            try {
              const userRef = doc(db, "users", currentUser.uid);
              const userSnap = await getDoc(userRef);
              if (userSnap.exists()) {
                const userData = userSnap.data();
                if (userData.firstName && userData.lastName) {
                  userName = `${userData.firstName} ${userData.lastName}`;
                }
              }
            } catch (err) {
              console.error("Error fetching user details for logout log:", err);
            }

            await logToSystem(orgId, "auth", {
              type: "AUTH",
              user: userName,
              email: currentUser.email,
              action: "Logout",
              details: "User signed out",
              status: "info",
            });

            sessionStorage.removeItem(`hasLoggedSignIn_${currentUser.uid}`);
          } catch (logError) {
            console.error("Error logging logout:", logError);
          }
      }

      await signOut(auth);
      setUser(null);
      setOrgId(null); // Clear orgId on logout
      setIsSignedIn(false);
      localStorage.removeItem("sessionExpiry");
      // Ensure any super admin session is cleared as well
      localStorage.removeItem("isSuperAdmin");
      localStorage.removeItem("orgName"); // Clear cached Org Name
      // Dispatch event to notify route guard
      window.dispatchEvent(new Event("superAdminStatusChanged"));
    } catch (error) {
      console.error("Error during logout:", error);
    }
  };

  return { isSignedIn, user, signUp, signIn, createOrganization, loading, logout, orgId };
};